import { create } from 'zustand';
import AuthService from '../../app/auth/Authorization.service';
import { clearLoggedUser, fetchLoggedUser } from '../../app/auth/loggedUser';
import type { Profile } from '../../app/auth/accessPolicy';

/**
 * Perfil do usuário logado, buscado **uma vez por carregamento de página**.
 *
 * Antes cada guarda de rota refazia `GET /auth/{token}` na montagem, com um
 * `<Spin>` de tela cheia enquanto isso. Com ~20 rotas guardadas isso viraria um
 * round-trip e um flash de spinner a cada navegação; daí o cache.
 *
 * A semente vem do `localStorage`, então a primeira pintura já tem resposta e
 * não pisca. `AuthService.getUserProfile()`/`setUserProfile()` existiam desde
 * sempre em `Authorization.service.ts` sem nenhum call site — a chave
 * `userProfile` nunca chegava a ser escrita. Agora é.
 *
 * ⚠️ **`AuthService.logoutAndRedirect()` limpa essa chave.** Sem isso, o
 * próximo login em outro perfil na mesma máquina nasceria com a semente errada
 * até a revalidação — um `basico` veria as telas de admin por um instante.
 *
 * Isto é cache de UI. Quem decide de verdade é o backend, a cada requisição.
 */

interface ProfileState {
  profile: Profile | null;
  status: 'idle' | 'loading' | 'ready' | 'error';
  /** Busca o perfil uma vez; chamadas concorrentes compartilham a promessa. */
  load: () => Promise<void>;
  /** Semear direto (o login já tem o usuário em mãos). */
  set: (profile: Profile | null) => void;
  clear: () => void;
}

function readSeed(): Profile | null {
  try {
    return (AuthService.getUserProfile() as Profile | null) ?? null;
  } catch {
    // localStorage pode lançar (modo privado, cookies bloqueados).
    return null;
  }
}

function writeSeed(profile: Profile | null) {
  try {
    if (profile) AuthService.setUserProfile(profile);
    else AuthService.removeUserProfile();
  } catch {
    /* idem */
  }
}

/**
 * Dedupe em escopo de módulo — precisa sobreviver a remontagens dos guardas.
 *
 * `inFlight` junta chamadas simultâneas; `loadedOnce` impede que a segunda
 * rota guardada dispare um novo GET depois que a primeira já respondeu. Sem ele
 * o cache não cacheia nada: seria de novo uma requisição por navegação.
 */
let inFlight: Promise<void> | null = null;
let loadedOnce = false;

const seed = readSeed();

export const useProfileStore = create<ProfileState>((set) => ({
  profile: seed,
  // Com semente já dá para decidir; a revalidação corrige em segundo plano.
  status: seed ? 'ready' : 'idle',

  set: (profile) => {
    // O login acabou de buscar o usuário; não há o que revalidar.
    loadedOnce = true;
    writeSeed(profile);
    set({ profile, status: 'ready' });
  },

  clear: () => {
    inFlight = null;
    loadedOnce = false;
    clearLoggedUser();
    writeSeed(null);
    set({ profile: null, status: 'idle' });
  },

  load: () => {
    if (inFlight) return inFlight;
    if (loadedOnce) return Promise.resolve();

    const token = AuthService.getAccessToken();
    if (!token) {
      set({ profile: null, status: 'ready' });
      return Promise.resolve();
    }

    set((state) => (state.status === 'ready' ? state : { ...state, status: 'loading' }));

    // `fetchLoggedUser` é a leitura compartilhada: o `getUser` do Redux, que
    // alimenta o menu, passa pela mesma e não gasta um segundo round-trip.
    inFlight = fetchLoggedUser(token)
      .then((user) => {
        const profile = (user?.profile as Profile | undefined) ?? null;
        writeSeed(profile);
        set({ profile, status: 'ready' });
      })
      .catch(() => {
        // 401 já foi tratado pelo interceptor (que redireciona). Aqui só
        // marcamos o erro; o guarda decide com a semente que tiver.
        set((state) => ({ ...state, status: state.profile ? 'ready' : 'error' }));
      })
      .finally(() => {
        loadedOnce = true;
        inFlight = null;
      });

    return inFlight;
  },
}));
